import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/apiService';
import type { Exercise, Category } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../contexts/ToastContext';
import { Video } from 'lucide-react';
import './CoachExercises.css';

export const CoachExercises: React.FC = () => {
  const toast = useToast();
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Exercise modal state
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);
  const [exerciseForm, setExerciseForm] = useState({
    name: '',
    category_id: 0,
    description: '',
    imageUrl: '',
    videoUrl: ''
  });

  const { t } = useTranslation();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [exercisesData, categoriesData] = await Promise.all([
        apiService.getExercises(),
        apiService.getCategories(),
      ]);
      setExercises(exercisesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Exercise handlers
  const handleCreateExercise = () => {
    setEditingExercise(null);
    setExerciseForm({
      name: '',
      category_id: categories[0]?.id || 0,
      description: '',
      imageUrl: '',
      videoUrl: ''
    });
    setShowExerciseModal(true);
  };

  const handleEditExercise = (exercise: Exercise) => {
    setEditingExercise(exercise);
    setExerciseForm({
      name: exercise.name,
      category_id: getCategoryId(exercise),
      description: exercise.description || '',
      imageUrl: exercise.imageUrl || '',
      videoUrl: exercise.videoUrl || ''
    });
    setShowExerciseModal(true);
  };

  const handleSaveExercise = async () => {
    try {
      if (!exerciseForm.name.trim()) {
        toast.error('El nombre del ejercicio es requerido');
        return;
      }
      if (!exerciseForm.category_id) {
        toast.error('La categoría es requerida');
        return;
      }

      if (editingExercise) {
        await apiService.updateExercise(editingExercise.id, exerciseForm);
        toast.success(t('exerciseUpdated'));
      } else {
        await apiService.createExercise(exerciseForm);
        toast.success(t('exerciseCreated'));
      }

      setShowExerciseModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving exercise:', error);
      toast.error(t('error'));
    }
  };

  const handleDeleteExercise = async (id: number) => {
    if (!window.confirm(t('confirmDelete'))) {
      return;
    }

    try {
      await apiService.deleteExercise(id);
      toast.success(t('exerciseDeleted'));
      loadData();
    } catch (error) {
      console.error('Error deleting exercise:', error);
      toast.error(t('error'));
    }
  };

  const getCategoryId = (exercise: Exercise): number => {
    // Try to get category_id from the exercise, or from the nested category object
    return exercise.category_id || exercise.category?.id || 0;
  };

  const getCategoryName = (exercise: Exercise): string => {
    // First try to get from nested category object
    if (exercise.category?.name) {
      return exercise.category.name;
    }
    // Otherwise look it up in the categories array
    const categoryId = getCategoryId(exercise);
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || 'Sin categoría';
  };

  if (isLoading) {
    return <div className="text-center p-4">{t('loading')}</div>;
  }

  return (
    <div className="coach-exercises-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('exercises')}</h1>
          <p className="page-subtitle">Gestiona los ejercicios disponibles</p>
        </div>
        <Button onClick={handleCreateExercise} variant="primary">
          + {t('exercise')}
        </Button>
      </div>

      <div className="exercises-grid">
        {exercises.length === 0 ? (
          <Card>
            <p className="text-center">No hay ejercicios disponibles</p>
          </Card>
        ) : (
          exercises.map((exercise) => (
            <Card key={exercise.id} className={`exercise-card ${exercise.imageUrl ? 'with-image' : ''}`}>
              <div className="exercise-top-section">
                {exercise.imageUrl && (
                  <div className="exercise-image-container">
                    <img
                      src={exercise.imageUrl}
                      alt={exercise.name}
                      className="exercise-image"
                      onError={(e) => {
                        const container = e.currentTarget.parentElement;
                        if (container) {
                          container.style.display = 'none';
                        }
                      }}
                    />
                  </div>
                )}
                <div className="exercise-header-content">
                  <h3 className="exercise-name">{exercise.name}</h3>
                  <p className="exercise-category">{getCategoryName(exercise)}</p>
                </div>
              </div>
              {exercise.description && (
                <p className="exercise-description">{exercise.description}</p>
              )}
              <div className="exercise-actions">
                {exercise.videoUrl && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => window.open(exercise.videoUrl, '_blank', 'noopener,noreferrer')}
                    title={t('watchVideo')}
                  >
                    <Video size={16} />
                    {t('video')}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleEditExercise(exercise)}
                >
                  {t('edit')}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteExercise(exercise.id)}
                >
                  {t('delete')}
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Exercise Modal */}
      {showExerciseModal && (
        <div className="modal-overlay" onClick={() => setShowExerciseModal(false)}>
          <div className="modal-content modal-content-large" onClick={(e) => e.stopPropagation()}>
            <h2>{editingExercise ? t('edit') : t('create')} {t('exercise')}</h2>
            <div className="modal-form">
              <Input
                label={t('name')}
                value={exerciseForm.name}
                onChange={(e) => setExerciseForm({ ...exerciseForm, name: e.target.value })}
                placeholder="Nombre del ejercicio"
              />
              <div className="input-wrapper">
                <label className="input-label">{t('category')}</label>
                <select
                  className="input"
                  value={exerciseForm.category_id}
                  onChange={(e) => setExerciseForm({ ...exerciseForm, category_id: Number(e.target.value) })}
                >
                  <option value={0} disabled>Seleccionar categoría</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="input-wrapper">
                <label className="input-label">{t('description')}</label>
                <textarea
                  className="input textarea"
                  value={exerciseForm.description}
                  onChange={(e) => setExerciseForm({ ...exerciseForm, description: e.target.value })}
                  placeholder={t('description')}
                  rows={3}
                />
              </div>
              <Input
                label={t('imageUrl')}
                value={exerciseForm.imageUrl}
                onChange={(e) => setExerciseForm({ ...exerciseForm, imageUrl: e.target.value })}
                placeholder="https://ejemplo.com/imagen.jpg"
              />
              <Input
                label={t('videoUrl')}
                value={exerciseForm.videoUrl}
                onChange={(e) => setExerciseForm({ ...exerciseForm, videoUrl: e.target.value })}
                placeholder="https://ejemplo.com/video.mp4"
              />
              {exerciseForm.imageUrl && (
                <div className="image-preview">
                  <label className="input-label">{t('imagePreview')}</label>
                  <img
                    src={exerciseForm.imageUrl}
                    alt={t('imagePreview')}
                    className="preview-image"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowExerciseModal(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary" onClick={handleSaveExercise}>
                {t('save')}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
